// Copied from platform-app components/pos/customerFacing/IllustrativePreview.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
import type { APosCustomerDisplayConfig } from '@platform/shared-types';
import SeasonalEffectOverlay from './SeasonalEffectOverlay';
import ShimmerImage from '../../ShimmerImage';

/**
 * Which mark each saved key draws. Keys MUST match the thank-you animation enum
 * in APosCustomerDisplayConfigSchema, and the retired emoji keys map exactly as
 * AGENTIC_POS CustomerThankYouScreen's LEGACY_ANIMATION maps them — a terminal
 * saved before 2026-09-20 must preview what it will actually show.
 */
const THANKYOU_MARK: Record<string, 'check' | 'ripple' | 'burst' | 'sparkle' | 'heart'> = {
  check: 'check', ripple: 'ripple', burst: 'burst', sparkle: 'sparkle', heart: 'heart',
  clap: 'burst', highfive: 'burst', gift: 'burst',
  smiley: 'ripple', pray: 'ripple', thumbsup: 'ripple',
};

type ThemeName = 'light' | 'dark';

interface TokenMap {
  bg: string; surface: string; border: string; text: string; muted: string;
  ok: string; okSoft: string; okBorder: string; track: string; accent: string;
}

/**
 * Customer-display tokens (spec Part 1 "Theme tokens"), values from the approved
 * mockup's Light and Dark pages. Status colours are fixed per theme; only the
 * accent follows `accentColor`. AGENTIC_POS customerDisplay.css owns the real
 * screens — this is the fallback drawing when the till iframe is unavailable.
 */
export const CD_PREVIEW_TOKENS: Record<ThemeName, TokenMap> = {
  light: {
    bg: '#F4F4F5', surface: '#FFFFFF', border: '#E4E4E7', text: '#18181B', muted: '#52525B',
    ok: '#15803D', okSoft: '#DCFCE7', okBorder: '#BBF7D0', track: '#E4E4E7', accent: '#C8102E',
  },
  dark: {
    bg: '#0B0B0C', surface: '#18181B', border: '#2A2A30', text: '#F4F4F5', muted: '#A1A1AA',
    ok: '#4ADE80', okSoft: 'rgba(34,197,94,.16)', okBorder: 'rgba(34,197,94,.30)', track: '#2A2A30', accent: '#E11D48',
  },
};

const themeOf = (config: APosCustomerDisplayConfig): ThemeName => (config.theme === 'dark' ? 'dark' : 'light');

export function previewAccent(config: APosCustomerDisplayConfig): string {
  const custom = (config.accentColor || '').trim();
  return custom || CD_PREVIEW_TOKENS[themeOf(config)].accent;
}

function tokenStyle(theme: ThemeName, accent: string): React.CSSProperties {
  const t = CD_PREVIEW_TOKENS[theme];
  const inkBase = theme === 'dark' ? '#FFFFFF' : '#000000';
  return {
    '--cd-bg': t.bg,
    '--cd-surface': t.surface,
    '--cd-border': t.border,
    '--cd-text': t.text,
    '--cd-muted': t.muted,
    '--cd-ok': t.ok,
    '--cd-ok-soft': t.okSoft,
    '--cd-ok-border': t.okBorder,
    '--cd-track': t.track,
    '--cd-accent': accent,
    '--cd-accent-soft': `color-mix(in srgb, ${accent} ${theme === 'dark' ? 16 : 10}%, ${t.surface})`,
    '--cd-accent-border': `color-mix(in srgb, ${accent} 38%, ${t.surface})`,
    '--cd-accent-ink': `color-mix(in srgb, ${accent} ${theme === 'dark' ? 45 : 80}%, ${inkBase})`,
  } as React.CSSProperties;
}

type QrPosition = NonNullable<NonNullable<APosCustomerDisplayConfig['qr']>['position']>;

const TOP_BAR_H = 36;

/** Corner placement that never covers the top bar or the promo strip. */
function qrStyle(position: QrPosition, opts: { topBar: boolean; promo: boolean }): React.CSSProperties {
  const top = opts.topBar ? TOP_BAR_H + 10 : 10;
  const bottom = opts.promo ? 40 : 10;
  switch (position) {
    case 'top-left': return { top, left: 10 };
    case 'top-center': return { top, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-left': return { bottom, left: 10 };
    case 'bottom-center': return { bottom, left: '50%', transform: 'translateX(-50%)' };
    case 'bottom-right': return { bottom, right: 10 };
    case 'top-right':
    default: return { top, right: 10 };
  }
}

const CheckIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

interface Props { config: APosCustomerDisplayConfig; screen: 'idle' | 'thankyou' }

export default function IllustrativePreview({ config, screen }: Props) {
  const theme = themeOf(config);
  const accent = previewAccent(config);
  const storeName = config.idle.storeName || 'Store name';
  const initial = (config.idle.storeName || 'R').trim().charAt(0).toUpperCase();
  const logoFit = (config.idle.logoFit || 'contain') as 'contain' | 'cover';
  const playlist = config.idle.mode === 'playlist';
  const promoOnIdle = !!config.promo?.enabled && !playlist && screen === 'idle';
  const qrOn = !!config.qr?.enabled;
  const qrPosition: QrPosition = config.qr?.position || 'top-right';

  const logoTile = (size: number, testId: string, radius: number) => (
    <div
      data-testid={testId}
      style={{
        width: size, height: size, borderRadius: radius, flexShrink: 0, overflow: 'hidden',
        background: 'var(--cd-accent)', color: '#FFFFFF', display: 'grid', placeItems: 'center',
        fontSize: Math.round(size * 0.42), fontWeight: 800,
      }}
    >
      {config.idle.logoUrl
        ? <ShimmerImage src={config.idle.logoUrl} alt="" width="100%" height="100%" objectFit={logoFit} variant={theme} />
        : initial}
    </div>
  );

  const qrTile = qrOn && (
    <div
      data-testid="cfp-qr"
      data-position={qrPosition}
      style={{
        position: 'absolute', zIndex: 5, ...qrStyle(qrPosition, { topBar: screen === 'thankyou', promo: promoOnIdle }),
        width: 48, padding: 4, borderRadius: 8, background: '#FFFFFF', border: '1px solid var(--cd-border)',
        boxShadow: '0 1px 4px rgba(0,0,0,.25)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
      }}
    >
      <div
        style={{
          width: 38, height: 38, borderRadius: 4, backgroundColor: '#0F172A',
          backgroundImage: 'linear-gradient(45deg,#fff 25%,transparent 25%,transparent 75%,#fff 75%),linear-gradient(45deg,#fff 25%,transparent 25%,transparent 75%,#fff 75%)',
          backgroundSize: '10px 10px', backgroundPosition: '0 0, 5px 5px',
        }}
      />
      <div style={{ fontSize: 7, fontWeight: 700, color: '#334155', lineHeight: 1 }}>SCAN</div>
    </div>
  );

  const thankYouVisual = () => {
    const visual = config.thankYou?.visual ?? 'animation';
    const circle: React.CSSProperties = {
      width: 44, height: 44, borderRadius: 999, display: 'grid', placeItems: 'center', overflow: 'hidden', flexShrink: 0,
    };
    if (visual === 'logo') {
      return <div data-testid="cfp-ty-visual">{logoTile(44, 'cfp-ty-logo', 999)}</div>;
    }
    if (visual === 'image') {
      if (config.thankYou?.imageUrl) {
        return (
          <div data-testid="cfp-ty-visual" style={{ ...circle, border: '1px solid var(--cd-border)' }}>
            <ShimmerImage src={config.thankYou.imageUrl} alt="" width="100%" height="100%" objectFit="cover" variant={theme} />
          </div>
        );
      }
      return (
        <div data-testid="cfp-ty-visual" style={{ ...circle, background: 'var(--cd-accent-soft)', color: 'var(--cd-accent-ink)', fontSize: 9, fontWeight: 700 }}>
          Image
        </div>
      );
    }
    const mark = THANKYOU_MARK[config.thankYou?.animation ?? 'check'] ?? 'check';
    const tick = <polyline points="37,50 46,60 65,39" fill="none" stroke="var(--cd-ok)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />;
    return (
      <div data-testid="cfp-ty-visual" data-mark={mark} style={{ ...circle, background: 'transparent' }}>
        <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }} aria-hidden="true">
          {mark === 'check' && (<><circle cx="50" cy="50" r="46" fill="var(--cd-ok-soft)" /><circle cx="50" cy="50" r="42" fill="none" stroke="var(--cd-ok)" strokeWidth="5" />{tick}</>)}
          {mark === 'ripple' && (<><circle cx="50" cy="50" r="44" fill="none" stroke="var(--cd-ok)" strokeWidth="3" opacity=".35" /><circle cx="50" cy="50" r="37" fill="none" stroke="var(--cd-ok)" strokeWidth="3" opacity=".6" /><circle cx="50" cy="50" r="30" fill="var(--cd-ok-soft)" />{tick}</>)}
          {mark === 'burst' && (<>{Array.from({ length: 12 }, (_, i) => { const a = (i * 30 * Math.PI) / 180; return <line key={i} x1={50 + Math.cos(a) * 32} y1={50 + Math.sin(a) * 32} x2={50 + Math.cos(a) * 46} y2={50 + Math.sin(a) * 46} stroke="var(--cd-accent)" strokeWidth="5" strokeLinecap="round" />; })}<circle cx="50" cy="50" r="27" fill="var(--cd-ok-soft)" />{tick}</>)}
          {mark === 'sparkle' && (<><path d="M50 16 L56 42 L82 50 L56 58 L50 84 L44 58 L18 50 L44 42 Z" fill="var(--cd-accent)" /><path d="M78 20 L81 30 L91 33 L81 36 L78 46 L75 36 L65 33 L75 30 Z" fill="var(--cd-accent)" opacity=".6" /><path d="M22 62 L25 72 L35 75 L25 78 L22 88 L19 78 L9 75 L19 72 Z" fill="var(--cd-accent)" opacity=".6" /></>)}
          {mark === 'heart' && (<path d="M50 87 C20 65 5 47 5 29 C5 16 15 7 27 7 C37 7 45 14 50 23 C55 14 63 7 73 7 C85 7 95 16 95 29 C95 47 80 65 50 87 Z" fill="#ef4444" />)}
        </svg>
      </div>
    );
  };

  return (
    <div
      data-testid="cfp-root"
      data-theme={theme}
      data-accent={accent}
      style={{
        ...tokenStyle(theme, accent),
        position: 'relative', aspectRatio: '16 / 9', borderRadius: 12, overflow: 'hidden',
        background: 'var(--cd-bg)', color: 'var(--cd-text)', display: 'flex', flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
      }}
    >
      <SeasonalEffectOverlay effect={config.effect} />
      {qrTile}

      {screen === 'idle' ? (
        playlist ? (
          <div
            data-testid="cfp-playlist"
            style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              background: 'var(--cd-accent-soft)', color: 'var(--cd-accent-ink)', fontWeight: 700, fontSize: 14,
            }}
          >
            Ad playlist
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, paddingBottom: promoOnIdle ? 44 : 16 }}>
              <div
                style={{
                  minWidth: '55%', maxWidth: '80%', padding: '18px 28px', borderRadius: 16,
                  background: 'var(--cd-surface)', border: '1px solid var(--cd-border)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center',
                }}
              >
                {logoTile(56, 'cfp-logo', 14)}
                <div data-testid="cfp-store" style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>{storeName}</div>
                <div data-testid="cfp-tagline" style={{ fontSize: 12, color: 'var(--cd-muted)' }}>{config.idle.tagline}</div>
              </div>
            </div>
            {promoOnIdle && (
              <div
                data-testid="cfp-promo"
                style={{
                  position: 'absolute', left: 0, right: 0, bottom: 0, padding: '8px 12px', textAlign: 'center',
                  fontSize: 12, fontWeight: 700, color: 'var(--cd-accent-ink)', background: 'var(--cd-accent-soft)',
                  borderTop: '1px solid var(--cd-accent-border)',
                }}
              >
                {config.promo?.text}
              </div>
            )}
          </>
        )
      ) : (
        <>
          <div
            data-testid="cfp-topbar"
            style={{
              height: TOP_BAR_H, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px',
              background: 'var(--cd-surface)', borderBottom: '1px solid var(--cd-border)',
            }}
          >
            {config.thankYou?.showLogo !== false && logoTile(22, 'cfp-ty-toplogo', 6)}
            <div data-testid="cfp-topbar-store" style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {storeName}
            </div>
            <span
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, height: 22, padding: '0 8px', borderRadius: 999,
                background: 'var(--cd-ok-soft)', border: '1px solid var(--cd-ok-border)', color: 'var(--cd-ok)',
                fontSize: 11, fontWeight: 700,
              }}
            >
              <CheckIcon size={11} />
              Complete
            </span>
          </div>
          <div style={{ flex: 1, minHeight: 0, padding: 10, display: 'flex' }}>
            <div
              style={{
                flex: 1, borderRadius: 14, padding: '14px 18px', background: 'var(--cd-surface)', border: '1px solid var(--cd-border)',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6,
              }}
            >
              {thankYouVisual()}
              <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.05, letterSpacing: -0.5 }}>Thank you!</div>
              <div data-testid="cfp-thankyou-msg" style={{ fontSize: 13, color: 'var(--cd-muted)' }}>{config.thankYou?.message}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <div style={{ flex: 1, height: 4, borderRadius: 999, background: 'var(--cd-track)', overflow: 'hidden' }}>
                  <div style={{ width: '40%', height: '100%', background: 'var(--cd-muted)' }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--cd-muted)', whiteSpace: 'nowrap' }}>Ready for next customer</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
